/**
 * Declare a mounted view's rows BY IDENTITY for as long as it is mounted
 * (feed-filters-per-view). `useFeedNeeds` for a view that renders an
 * explicit list — see `lib/feedFilter/tickerNeeds` for what the declaration
 * carries and why.
 *
 *   useTickerFeedNeeds('position-tracker', heldIds, { fold: 'view' });
 *
 * The fold reads the store (a ticker's sport, game and class are known only
 * once its row has landed), so the declaration is re-derived on store ticks
 * — trailing-edge throttled, like the account plane's DNL-009 recompute —
 * and re-published only when it actually changes. Re-derivation declares
 * OVER THE TOP, never clear-then-declare (see `useFeedNeeds` for why that
 * transient wedged hc1 on 2026-09-05); clearing is an unmount concern only.
 *
 * `ids` null = this view has not loaded its list yet: nothing is declared,
 * so the connection's other sources decide. An EMPTY list is a loaded view
 * with no rows: declared as such, so a tracker with no positions names no
 * rows instead of every row.
 */
import { useEffect, useMemo } from 'react';
import { useMarketStore } from '@/stores/marketStore';
import { declareFeedNeeds } from '@/stores/feedNeeds';
import { tickerFeedNeeds, type TickerNeedsOptions } from '@/lib/feedFilter/tickerNeeds';
import { feedNeedsKey } from './useFeedNeeds';

const RECOMPUTE_THROTTLE_MS = 2_000;

export function useTickerFeedNeeds(
  sourceId: string,
  ids: readonly string[] | null,
  opts: TickerNeedsOptions,
): void {
  // Fold to a primitive so a fresh array of the same ids is not a new need.
  // Memoized on the list's identity: callers hand a content-keyed memo, and
  // sorting per render is exactly the churn the tracker's sort-count test
  // guards against (#828).
  const idsKey = useMemo(() => (ids == null ? null : [...new Set(ids)].sort().join(' ')), [ids]);
  const fold = opts.fold;
  const classes = opts.classes;
  useEffect(() => {
    if (idsKey == null) return;
    const list = idsKey ? idsKey.split(' ') : [];
    let last = '';
    const publish = (): void => {
      const needs = tickerFeedNeeds(list, { fold, classes });
      const key = feedNeedsKey(needs);
      if (key === last) return;
      last = key;
      declareFeedNeeds(sourceId, needs);
    };
    publish();
    // Trailing-edge throttle over the store's tick counter: a tick inside the
    // window books ONE recompute for the window's end (a leading-edge drop
    // loses the row that lands last — the account plane's R11 lesson).
    let lastRun = Date.now();
    let pending: ReturnType<typeof setTimeout> | null = null;
    const onTick = (): void => {
      const since = Date.now() - lastRun;
      if (since >= RECOMPUTE_THROTTLE_MS) { lastRun = Date.now(); publish(); return; }
      if (pending != null) return;
      pending = setTimeout(() => { pending = null; lastRun = Date.now(); publish(); }, RECOMPUTE_THROTTLE_MS - since);
    };
    // View tests mock the store module partially (no `subscribe`); the
    // declaration itself is still made above, only the fold refresh is lost.
    const subscribe = (useMarketStore as { subscribe?: typeof useMarketStore.subscribe }).subscribe;
    const unsubscribe = typeof subscribe === 'function'
      ? subscribe((s, prev) => { if (s.version !== prev.version) onTick(); })
      : () => {};
    return () => {
      unsubscribe();
      if (pending != null) clearTimeout(pending);
    };
  }, [sourceId, idsKey, fold, classes]);
  useEffect(() => () => declareFeedNeeds(sourceId, null), [sourceId]);
}
